import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";

/**
 * Modern Functional PercentageScale Component
 */
const PercentageScale = ({
    value = 50,
    min = 0,
    max = 100,
    step = 1,
    onChange = () => {},
    textColor = "#ffffff",
    unit = "%",
    leftLabel = "0%",
    rightLabel = "100%",
}) => {
    const [currentValue, setCurrentValue] = useState(value);
    const [isDragging, setIsDragging] = useState(false);
    const trackRef = useRef(null);

    // Sync external prop changes
    useEffect(() => {
        if (value !== undefined) {
            setCurrentValue(value);
        }
    }, [value]);

    const updateValue = useCallback(
        (val) => {
            const steppedVal = Math.round((val - min) / step) * step + min;
            const clampedVal = Math.max(min, Math.min(max, steppedVal));
            setCurrentValue((prevVal) => {
                if (prevVal !== clampedVal) {
                    onChange(clampedVal);
                }
                return clampedVal;
            });
        },
        [min, max, step, onChange],
    );

    const getValueFromClientX = useCallback(
        (clientX) => {
            if (!trackRef.current) return min;
            const rect = trackRef.current.getBoundingClientRect();
            const clickX = clientX - rect.left;
            const ratio = Math.max(0, Math.min(1, clickX / rect.width));
            return min + ratio * (max - min);
        },
        [min, max],
    );

    const handlePointerDown = (e) => {
        setIsDragging(true);
        const rawVal = getValueFromClientX(e.clientX);
        updateValue(rawVal);
    };

    // Window event listeners while dragging
    useEffect(() => {
        if (!isDragging) return;

        const handlePointerMove = (e) => {
            const rawVal = getValueFromClientX(e.clientX);
            updateValue(rawVal);
        };

        const handlePointerUp = () => {
            setIsDragging(false);
        };

        window.addEventListener("pointermove", handlePointerMove);
        window.addEventListener("pointerup", handlePointerUp);
        window.addEventListener("pointercancel", handlePointerUp);

        return () => {
            window.removeEventListener("pointermove", handlePointerMove);
            window.removeEventListener("pointerup", handlePointerUp);
            window.removeEventListener("pointercancel", handlePointerUp);
        };
    }, [isDragging, getValueFromClientX, updateValue]);

    // Percentage ratio for fill & thumb position
    const percent = Math.max(
        0,
        Math.min(100, ((currentValue - min) / (max - min)) * 100),
    );

    return (
        <div
            className={`percentage-scale ${isDragging ? "is-dragging" : ""}`}
            style={{ color: textColor }}
        >
            {/* Header Labels (Positioned above 0% & 100% track ends) */}
            {(leftLabel || rightLabel) && (
                <div className="percentage-scale__labels-header">
                    <div className="percentage-scale__label-cell percentage-scale__label-cell--left">
                        <span className="percentage-scale__label-text">
                            {leftLabel}
                        </span>
                    </div>
                    <div className="percentage-scale__label-cell percentage-scale__label-cell--right">
                        <span className="percentage-scale__label-text">
                            {rightLabel}
                        </span>
                    </div>
                </div>
            )}

            {/* Padded Center Ruler Track Container */}
            <div className="percentage-scale__track-container">
                {/* Full-width horizontal background line */}
                <div className="percentage-scale__full-line" />

                <div
                    className="percentage-scale__track-wrapper"
                    ref={trackRef}
                    onPointerDown={handlePointerDown}
                >
                    {/* Outer Ruler Bar Container */}
                    <div className="percentage-scale__bar">
                        {/* Progress Fill */}
                        <div
                            className="percentage-scale__fill"
                            style={{ width: `${percent}%` }}
                        />

                        {/* Repeating Ruler Ticks Pattern */}
                        <div className="percentage-scale__ruler-ticks" />
                    </div>

                    {/* Thumb + Tooltip Floating Group */}
                    <div
                        className="percentage-scale__thumb-group"
                        style={{ left: `${percent}%` }}
                    >
                        {/* Circular White Thumb Handle */}
                        <div className="percentage-scale__thumb">
                            <svg
                                width="10"
                                height="12"
                                viewBox="0 0 10 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M3 2.5L1 6L3 9.5M7 2.5L9 6L7 9.5"
                                    stroke="#888888"
                                    strokeWidth="1.75"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>

                        {/* Floating Tooltip Box Below Thumb */}
                        <div className="percentage-scale__tooltip">
                            <div className="percentage-scale__tooltip-arrow" />
                            <div className="percentage-scale__tooltip-box">
                                {currentValue}
                                {unit}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

PercentageScale.propTypes = {
    value: PropTypes.number,
    min: PropTypes.number,
    max: PropTypes.number,
    step: PropTypes.number,
    onChange: PropTypes.func,
    textColor: PropTypes.string,
    unit: PropTypes.string,
    leftLabel: PropTypes.string,
    rightLabel: PropTypes.string,
};

export default PercentageScale;
